import { useEffect, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { shallow } from 'zustand/shallow';

import { useDynamicEntityCMSDataContext, useDynamicEntityStoreContext } from '../contexts';
import {
  convertMetadata,
  sanitiseContent,
  formatModulesConnected,
  omitKeys,
  OMIT_KEYS_NEW,
  OMIT_KEYS_EDIT,
  OMIT_KEYS_NEW_MODULE,
  OMIT_KEYS_EDIT_MODULE,
} from '../lib';
import getCSRFToken from '../../utils/CSRFToken';

import ConfirmationDialog from '../../confirmation-dialog/ConfirmationDialog';
import JSONModal from './JSONModal';

export default function PageOptionsBar({ isEdit, handleRefresh, isUndoRedoEnabled = true }) {
  const { isModule, endpoints } = useDynamicEntityCMSDataContext();

  const updateData = useDynamicEntityStoreContext((state) => state.updateData);
  const undo = useDynamicEntityStoreContext((state) => state.undo);
  const redo = useDynamicEntityStoreContext((state) => state.redo);
  const parentSlug = useDynamicEntityStoreContext((state) => state.parentSlug);
  const currentDataStateIndex = useDynamicEntityStoreContext((state) => state.currentDataStateIndex);
  const totalDataStateIndex = useDynamicEntityStoreContext((state) => state.totalDataStateIndex);
  const isError = useDynamicEntityStoreContext((state) => state.isError);
  const hasUnsavedChanges = useDynamicEntityStoreContext((state) => state.hasUnsavedChanges);

  const data = useDynamicEntityStoreContext((state) => state.data);
  const id = useDynamicEntityStoreContext((state) => state.data.id);
  const metadata = useDynamicEntityStoreContext((state) => state.data.metadata, shallow);
  const seo = useDynamicEntityStoreContext((state) => state.data.seo, shallow);
  const content = useDynamicEntityStoreContext((state) => state.data.content, shallow);
  const urlSlug = useDynamicEntityStoreContext((state) => state.data.url_slug);
  const initialModulesConnected = useDynamicEntityStoreContext((state) => state.initialData.modules_connected, shallow);
  const modulesConnected = useDynamicEntityStoreContext((state) => state.data.modules_connected, shallow);

  const { indexUrl, createUrl, pageUrl, moduleUrl, duplicateUrl } = endpoints;

  const getSavePayload = () => {
    if (isModule) {
      const keys = isEdit ? OMIT_KEYS_EDIT_MODULE : OMIT_KEYS_NEW_MODULE;
      const sanitisedData = omitKeys(data, keys);
      return {
        url: `${isEdit ? moduleUrl : createUrl}.json`,
        method: isEdit ? 'PUT' : 'POST',
        body: { dynamic_page_module: { ...sanitisedData, content: sanitiseContent(content) } },
      };
    } else {
      const keys = isEdit ? OMIT_KEYS_EDIT : OMIT_KEYS_NEW;
      const sanitisedData = omitKeys(data, keys);
      return {
        url: `${isEdit ? pageUrl : createUrl}.json`,
        method: isEdit ? 'PUT' : 'POST',
        body: {
          dynamic_page: {
            ...sanitisedData,
            url_slug: !isEdit && parentSlug ? (parentSlug === '/' ? urlSlug : `${parentSlug}/${urlSlug}`) : urlSlug,
            metadata: convertMetadata(metadata),
            content: sanitiseContent(content),
            dynamic_page_module_assignments_attributes: isEdit
              ? formatModulesConnected(modulesConnected, initialModulesConnected)
              : formatModulesConnected(modulesConnected),
          },
        },
      };
    }
  };

  const saveMutation = useMutation({
    mutationFn: async ({ isClose = false }) => {
      const { url, method, body } = getSavePayload();
      const res = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', ...getCSRFToken() },
        body: JSON.stringify(body),
      });

      const resData = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(resData, null, 2));

      return { res, resData, isClose };
    },
    onMutate: () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    },
    onSuccess: ({ res, resData, isClose }) => {
      if (isClose) return (window.location.href = indexUrl);
      if (res.status === 201) return (window.location.href = resData.redirect_url);
      window.location.reload();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const url = `${isModule ? moduleUrl : pageUrl}.json`;
      const res = await fetch(url, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', ...getCSRFToken() },
      });

      const resData = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(resData, null, 2));
    },
    onMutate: () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    },
    onSuccess: () => {
      window.location.replace(indexUrl);
    },
  });

  const handleUndo = async () => {
    await undo();
    handleRefresh();
  };

  const handleRedo = async () => {
    await redo();
    handleRefresh();
  };

  const handleBeforeUnload = useCallback((e) => {
    e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
  }, []);

  const handleDiscardChanges = () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.location.href = indexUrl;
  };

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const isMutating = saveMutation.isPending || deleteMutation.isPending;
  const isSaveDisabled = isError || !hasUnsavedChanges || isMutating;

  const confirmationDialogs = [
    {
      id: 'discardConfirmationModal-builder',
      title: 'Confirm leave',
      message: 'You have unsaved changes, are you sure you want to leave?',
      confirmButtonText: 'Leave',
      confirmButtonClass: 'btn-secondary',
      onConfirm: handleDiscardChanges,
      condition: true,
    },
    {
      id: 'saveConfirmationModal-builder',
      title: 'Confirm Save',
      message: 'Are you sure you want to save this page?',
      confirmButtonText: 'Save',
      confirmButtonClass: 'btn-primary',
      onConfirm: () => saveMutation.mutateAsync({ isClose: false }),
      condition: true,
    },
    {
      id: 'saveAndCloseConfirmationModal-builder',
      title: 'Confirm Save & Close',
      message: 'Are you sure you want to save and close this page?',
      confirmButtonText: 'Save & Close',
      confirmButtonClass: 'btn-primary',
      onConfirm: () => saveMutation.mutateAsync({ isClose: true }),
      condition: true,
    },
    {
      id: 'deleteConfirmationModal-builder',
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this page?',
      confirmButtonText: 'Delete',
      confirmButtonClass: 'btn-danger',
      onConfirm: () => deleteMutation.mutateAsync(),
      condition: isEdit,
    },
  ];

  const jsonModals = [
    { id: 'contentJSON-builder', title: 'Content JSON', label: 'Content', initialValue: content, onSave: (val) => updateData({ content: val }) },
    { id: 'seoJSON-builder', title: 'SEO JSON', label: 'SEO', initialValue: seo, onSave: (val) => updateData({ seo: val }) },
    { id: 'metadataJSON-builder', title: 'Metadata JSON', label: 'Metadata', initialValue: metadata, onSave: (val) => updateData({ metadata: val }) },
  ];

  return (
    <div className="d-flex gap-3">
      {isUndoRedoEnabled && (
        <div className="d-flex gap-2">
          <button className="btn btn-outline top-button" onClick={handleUndo} disabled={currentDataStateIndex <= 0}>
            <i className="fe fe-rotate-ccw" />
          </button>
          <button className="btn btn-outline top-button" onClick={handleRedo} disabled={currentDataStateIndex >= totalDataStateIndex}>
            <i className="fe fe-rotate-cw" />
          </button>
        </div>
      )}
      <div className="dropdown">
        <button className="btn" type="button" data-bs-toggle="dropdown" aria-expanded="false">
          <i className="fe fe-more-vertical" />
        </button>
        <ul className="dropdown-menu">
          <li>
            {hasUnsavedChanges ? (
              <button className="dropdown-item" data-bs-toggle="modal" data-bs-target="#discardConfirmationModal-builder">
                Discard Changes
              </button>
            ) : (
              <a className="dropdown-item" href={indexUrl}>
                Discard Changes
              </a>
            )}
          </li>
          {isEdit && (
            <>
              <li>
                <hr className="dropdown-divider" />
              </li>
              <li>
                <button className="dropdown-item" data-bs-toggle="modal" data-bs-target="#deleteConfirmationModal-builder">
                  Delete
                </button>
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>
              <li>
                <a className="dropdown-item" href={duplicateUrl.replace(':id', id)}>
                  Duplicate
                </a>
              </li>
            </>
          )}
          <li>
            <hr className="dropdown-divider" />
          </li>
          <li>
            <h4 className="dropdown-item text-muted fw-bolder text-center">Show JSON</h4>
          </li>
          {jsonModals.map((modal) => (
            <li key={modal.id}>
              <button className="dropdown-item" data-bs-toggle="modal" data-bs-target={`#${modal.id}`}>
                {modal.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="d-flex gap-3 align-items-center justify-content-center">
        <button className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#saveConfirmationModal-builder" disabled={isSaveDisabled}>
          {isMutating ? '...' : 'Save'}
        </button>
        <button className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#saveAndCloseConfirmationModal-builder" disabled={isSaveDisabled}>
          {isMutating ? '...' : 'Save & Close'}
        </button>
      </div>
      {confirmationDialogs.map(({ condition, ...props }) => condition && <ConfirmationDialog key={props.id} {...props} />)}
      {jsonModals.map((props) => (
        <JSONModal key={props.id} {...props} />
      ))}
    </div>
  );
}
